import dayjs from 'dayjs';
import type {
  Activity,
  FocusObject,
  FocusType,
  Reminder,
  ScheduleEvent,
  SourceApp,
  SourceEntity,
  WorkbenchState,
  WorkItem,
} from './types';

const entitySeeds: Array<
  Omit<SourceEntity, 'latestChange' | 'nextNodeDate' | 'tags' | 'summary'> & {
    tags?: string[];
    summary?: string;
  }
> = [
  {
    id: 'project-suzhou-robot',
    sourceApp: 'investment',
    objectType: 'project',
    name: '苏州精控机器人',
    subtitle: '高精度运动控制与机器人关节模组',
    industry: '智能制造',
    stage: '尽调中',
    owner: '张伟',
    riskLevel: 'medium',
    nextNode: '投决会',
    amount: 5000,
    location: '苏州',
    tags: ['机器人', 'A轮'],
    summary: '技术尽调接近完成，等待核心客户验证材料与投决结论。',
  },
  {
    id: 'company-suzhou-lanxin',
    sourceApp: 'portfolio',
    objectType: 'company',
    name: '苏州澜芯半导体',
    subtitle: '先进封装与功率器件',
    industry: '半导体',
    stage: '投后管理',
    owner: '李娜',
    riskLevel: 'high',
    nextNode: '经营专题会',
    amount: 8200,
    location: '苏州',
    tags: ['高风险', 'B轮'],
    summary: '现金可支撑约 8.4 个月，核心客户验证延期，需跟踪回款与融资计划。',
  },
  {
    id: 'project-zhikong-energy',
    sourceApp: 'investment',
    objectType: 'project',
    name: '智控新能源 B 轮项目',
    subtitle: '储能系统智能控制平台',
    industry: '新能源',
    stage: '待投决',
    owner: '张伟',
    riskLevel: 'medium',
    nextNode: '投资决策会',
    amount: 5000,
    location: '上海',
    tags: ['新能源', 'B轮'],
    summary: '商业尽调完成，明日进入投资决策会。',
  },
  {
    id: 'exit-hangzhou-ruisi',
    sourceApp: 'exit',
    objectType: 'exit',
    name: '杭州锐思工业软件',
    subtitle: '制造业工业软件平台',
    industry: '工业软件',
    stage: '回款中',
    owner: '刘洋',
    riskLevel: 'high',
    nextNode: '第二期回款',
    amount: 3060,
    location: '杭州',
    tags: ['退出', '回款逾期'],
    summary: '股权转让第二期回款逾期 3 天，需评估违约条款。',
  },
  {
    id: 'fund-ningbo-guidance',
    sourceApp: 'fund',
    objectType: 'fund',
    name: '宁波产业引导基金',
    subtitle: '智造成长基金一期核心 LP',
    industry: '综合',
    stage: '内部决策',
    owner: '王晨',
    riskLevel: 'medium',
    nextNode: '确认认缴金额',
    amount: 5000,
    location: '宁波',
    tags: ['LP', '引导基金'],
    summary: '预计认缴 5,000 万元，正在履行内部决策流程。',
  },
  {
    id: 'research-humanoid',
    sourceApp: 'research',
    objectType: 'research',
    name: '人形机器人核心零部件投资专题',
    subtitle: '行业专题研究',
    industry: '机器人',
    stage: '研究中',
    owner: '王若曦',
    riskLevel: 'low',
    nextNode: '阶段评审',
    location: '上海',
    tags: ['行业研究', '人形机器人'],
    summary: '新增潜在标的 3 家，核心观点已完成一次验证。',
  },
  {
    id: 'company-yuntu-logistics',
    sourceApp: 'portfolio',
    objectType: 'company',
    name: '云途物流',
    subtitle: '智能供应链服务商',
    industry: '物流科技',
    stage: '投后管理',
    owner: '李娜',
    riskLevel: 'high',
    nextNode: '财务风险复盘',
    amount: 4600,
    location: '深圳',
    tags: ['财务风险'],
    summary: '经营现金流连续两个季度为负，融资窗口缩短。',
  },
  {
    id: 'company-xingchen-chip',
    sourceApp: 'exit',
    objectType: 'exit',
    name: '星辰半导体',
    subtitle: '模拟芯片设计公司',
    industry: '半导体',
    stage: '上市辅导期',
    owner: '刘洋',
    riskLevel: 'medium',
    nextNode: '提交上市申请',
    amount: 14820,
    location: '无锡',
    tags: ['Pre-IPO'],
    summary: '上市辅导进展正常，等待中介机构材料会签。',
  },
  {
    id: 'lp-qiming-government',
    sourceApp: 'fund',
    objectType: 'lp',
    name: '启明创投人民币四期',
    subtitle: '重点合作基金',
    industry: '综合',
    stage: '投资期',
    owner: '王晨',
    riskLevel: 'low',
    nextNode: '组合中期复盘',
    amount: 22000,
    location: '北京',
    tags: ['基金', '合作'],
    summary: '基金运行稳健，计划开展季度组合复盘。',
  },
  {
    id: 'resource-semiconductor-alliance',
    sourceApp: 'research',
    objectType: 'resource',
    name: '半导体产业联盟',
    subtitle: '行业资源网络',
    industry: '半导体',
    stage: '合作中',
    owner: '王若曦',
    riskLevel: 'low',
    nextNode: '闭门交流会',
    location: '上海',
    tags: ['产业资源'],
    summary: '已确认参加下一期先进封装闭门交流活动。',
  },
];

const extraNames = [
  '未来动力科技',
  '深度智能科技',
  '华东先进制造基金',
  '微光半导体',
  '启航自动化',
  '远景医疗科技',
  '晨星新材料',
  '青岚储能',
  '昆仑工业软件',
  '汇智生物医药',
  '海岳新能源',
  '北辰机器人',
  '领航数字科技',
  '清源环保',
  '远川航空材料',
  '澄明医疗',
  '长川精密',
  '瀚海智能',
  '元启资本',
  '启辰资本',
  '产业并购机会专题',
  'AI 医疗器械专题',
  '新能源出海专题',
  '先进封装专家网络',
  '长三角产业引导基金',
  '苏州科创母基金',
  '宁德智造基金',
  '华南成长基金',
  '卓越传感器',
  '天工视觉',
  '云帆科技',
  '明略材料',
];

const apps: SourceApp[] = [
  'investment',
  'portfolio',
  'fund',
  'exit',
  'research',
];
const focusTypes: FocusType[] = [
  'project',
  'company',
  'fund',
  'exit',
  'research',
  'resource',
];
const stages = ['初筛中', '立项中', '尽调中', '投决中', '投后管理', '退出准备'];
const industries = [
  '人工智能',
  '智能制造',
  '半导体',
  '新能源',
  '医疗健康',
  '企业服务',
];
const owners = ['张伟', '李娜', '王晨', '刘洋', '王若曦', '陈可'];

const iso = (base: dayjs.Dayjs, dayOffset: number, hour = 9, minute = 0) =>
  base
    .add(dayOffset, 'day')
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0)
    .toISOString();

const createEntities = (base: dayjs.Dayjs): SourceEntity[] => {
  const seeded = entitySeeds.map((item, index) => ({
    ...item,
    latestChange: index % 2 ? '1天前更新关键进展' : '3小时前更新经营数据',
    nextNodeDate: iso(base, (index % 12) + 1, 10 + (index % 7)),
    tags: item.tags ?? [item.industry],
    summary:
      item.summary ?? `${item.name}当前处于${item.stage}，整体进展符合预期。`,
  }));
  const generated = extraNames.map((name, index): SourceEntity => {
    const sourceApp = apps[index % apps.length];
    return {
      id: `entity-${index + 1}`,
      sourceApp,
      objectType: focusTypes[index % focusTypes.length],
      name,
      subtitle: `${industries[index % industries.length]}重点业务对象`,
      industry: industries[index % industries.length],
      stage: stages[index % stages.length],
      owner: owners[index % owners.length],
      riskLevel: index % 11 === 0 ? 'high' : index % 4 === 0 ? 'medium' : 'low',
      latestChange: `${(index % 6) + 1}天前更新业务进展`,
      nextNode: ['项目评审', '经营复盘', 'LP 沟通', '交割检查', '专题评审'][
        index % 5
      ],
      nextNodeDate: iso(base, (index % 25) + 1, 9 + (index % 8)),
      amount: 1200 + index * 380,
      location: ['上海', '苏州', '北京', '深圳', '杭州'][index % 5],
      tags: [
        industries[index % industries.length],
        stages[index % stages.length],
      ],
      summary: `${name}是当前重点跟踪的${industries[index % industries.length]}业务对象，下一节点为${['项目评审', '经营复盘', 'LP 沟通'][index % 3]}。`,
    };
  });
  return [...seeded, ...generated];
};

const taskSeeds: Array<Partial<WorkItem> & Pick<WorkItem, 'id' | 'title'>> = [
  {
    id: 'task-suzhou-due-diligence',
    title: '苏州精控机器人技术尽调结论确认',
    entityId: 'project-suzhou-robot',
    relatedObject: '苏州精控机器人',
    sourceApp: 'investment',
    type: '尽调任务',
    priority: 'high',
    nextAction: '复核技术结论并提交投决材料',
  },
  {
    id: 'task-lanxin-q2',
    title: '苏州澜芯半导体 Q2 经营数据复核',
    entityId: 'company-suzhou-lanxin',
    relatedObject: '苏州澜芯半导体',
    sourceApp: 'portfolio',
    type: '投后报告',
    priority: 'high',
    nextAction: '核对现金流和客户验证进展',
  },
  {
    id: 'task-qichen-feedback',
    title: '启辰资本机器人项目推荐反馈',
    entityId: 'entity-20',
    relatedObject: '启辰资本',
    sourceApp: 'research',
    type: '资源跟进',
    priority: 'medium',
    nextAction: '回复项目反馈并安排交流',
  },
  {
    id: 'task-zhikong-decision',
    title: '智控新能源 B 轮项目投决材料确认',
    entityId: 'project-zhikong-energy',
    relatedObject: '智控新能源 B 轮项目',
    sourceApp: 'investment',
    type: '决策审批',
    priority: 'high',
    nextAction: '检查投决材料完整性',
  },
  {
    id: 'task-ningbo-commitment',
    title: '宁波产业引导基金认缴金额确认',
    entityId: 'fund-ningbo-guidance',
    relatedObject: '宁波产业引导基金',
    sourceApp: 'fund',
    type: 'LP 沟通',
    priority: 'high',
    nextAction: '确认认缴口径与决策进度',
  },
  {
    id: 'task-ruisi-payment',
    title: '杭州锐思第二期回款催收',
    entityId: 'exit-hangzhou-ruisi',
    relatedObject: '杭州锐思工业软件',
    sourceApp: 'exit',
    type: '回款跟进',
    priority: 'high',
    nextAction: '启动违约条款沟通',
  },
];

const createWorkItems = (
  base: dayjs.Dayjs,
  entities: SourceEntity[],
): WorkItem[] =>
  Array.from({ length: 80 }, (_, index) => {
    const seed = taskSeeds[index];
    const entity = entities[index % entities.length];
    const dayOffset = index < 2 ? 0 : index % 9 === 0 ? -2 : (index % 18) - 3;
    return {
      id: seed?.id ?? `task-${index + 1}`,
      title:
        seed?.title ??
        `${entity.name}${['进展复核', '材料补充', '会议准备', '跟进确认'][index % 4]}`,
      sourceApp: seed?.sourceApp ?? entity.sourceApp,
      entityId: seed?.entityId ?? entity.id,
      relatedObject: seed?.relatedObject ?? entity.name,
      type: seed?.type ?? ['任务', '评审', '审批', '跟进'][index % 4],
      priority:
        seed?.priority ??
        (index % 7 === 0 ? 'high' : index % 3 === 0 ? 'low' : 'medium'),
      owner: owners[index % owners.length],
      initiator: owners[(index + 2) % owners.length],
      dueAt: iso(base, dayOffset, 10 + (index % 9), index % 2 ? 30 : 0),
      status:
        index > 67
          ? 'completed'
          : index % 5 === 0
            ? 'inProgress'
            : 'notStarted',
      nextAction:
        seed?.nextAction ?? `完成${entity.nextNode}前置准备并反馈结论`,
      general: index > 72,
    };
  });

const meetingTitles = [
  '投决会',
  'LP 沟通会',
  '项目尽调访谈',
  '投后经营专题会',
  '退出方案评审',
  '行业专家交流',
];
const createSchedules = (
  base: dayjs.Dayjs,
  entities: SourceEntity[],
): ScheduleEvent[] =>
  Array.from({ length: 30 }, (_, index) => {
    const entity = entities[index % entities.length];
    const startHour = 9 + ((index * 2) % 9);
    return {
      id:
        index === 0 ? 'schedule-investment-committee' : `schedule-${index + 1}`,
      title:
        index === 0
          ? '投决会：智控新能源 B 轮项目'
          : `${meetingTitles[index % meetingTitles.length]}：${entity.name}`,
      sourceApp: entity.sourceApp,
      entityId: entity.id,
      date: base.add((index % 14) - 3, 'day').format('YYYY-MM-DD'),
      startTime: `${String(startHour).padStart(2, '0')}:${index % 2 ? '30' : '00'}`,
      endTime: `${String(Math.min(startHour + 1, 19)).padStart(2, '0')}:${index % 2 ? '30' : '00'}`,
      type: meetingTitles[index % meetingTitles.length],
      location:
        index % 3 === 0
          ? '线上会议'
          : `会议室 ${String.fromCharCode(65 + (index % 4))}`,
      participants: owners.slice(0, 3 + (index % 3)),
      agenda: [
        '项目进展汇报',
        '核心问题讨论',
        '风险与估值评估',
        '下一步工作计划',
      ],
      personal: index > 25,
      relatedTaskIds: index === 0 ? ['task-zhikong-decision'] : [],
    };
  });

const createFocusObjects = (
  base: dayjs.Dayjs,
  entities: SourceEntity[],
): FocusObject[] =>
  entities.slice(1, 31).map((entity, index) => ({
    id: `focus-${index + 1}`,
    entityId: entity.id,
    type: entity.objectType,
    level: entity.riskLevel === 'none' ? 'low' : entity.riskLevel,
    followedAt: iso(base, -(index % 20)),
  }));

const reminderTitles = [
  '经营现金流风险',
  '尽调材料待审核',
  '回款计划逾期',
  '投决会议临近',
  '数据报送提醒',
];
const createReminders = (
  base: dayjs.Dayjs,
  entities: SourceEntity[],
): Reminder[] =>
  Array.from({ length: 40 }, (_, index) => {
    const entity =
      index === 0
        ? (entities.find((item) => item.id === 'company-suzhou-lanxin') ??
          entities[0])
        : entities[index % entities.length];
    const level =
      index === 0 || entity.riskLevel === 'high'
        ? 'urgent'
        : index % 3 === 0
          ? 'important'
          : index % 4 === 0
            ? 'info'
            : 'normal';
    return {
      id: index === 0 ? 'reminder-lanxin-cash' : `reminder-${index + 1}`,
      title:
        index === 0
          ? '苏州澜芯现金安全线预警'
          : `${entity.name}：${reminderTitles[index % reminderTitles.length]}`,
      description:
        index === 0
          ? '现金可支撑仅 8.4 个月，核心客户验证延期。'
          : `${entity.summary}`,
      suggestion:
        index === 0
          ? '建议召开经营专题会并更新未来 12 个月现金流预测。'
          : `建议在${entity.nextNode}前完成负责人确认。`,
      sourceApp: entity.sourceApp,
      entityId: entity.id,
      level,
      status: index % 6 === 0 ? 'read' : 'unread',
      occurredAt: iso(base, -(index % 7), 8 + (index % 9), 15),
    };
  });

const activityActions = [
  '更新了业务进展',
  '提交了阶段材料',
  '完成了关键访谈',
  '新增了风险提示',
  '确认了下一节点',
];
const createActivities = (
  base: dayjs.Dayjs,
  entities: SourceEntity[],
): Activity[] =>
  Array.from({ length: 100 }, (_, index) => {
    const entity = entities[index % entities.length];
    return {
      id: `activity-${index + 1}`,
      title: `${entity.name}${activityActions[index % activityActions.length]}`,
      description: `${entity.owner}已更新${entity.stage}阶段信息，下一节点为${entity.nextNode}。`,
      sourceApp: entity.sourceApp,
      entityId: entity.id,
      objectName: entity.name,
      occurredAt: iso(
        base,
        -Math.floor(index / 6),
        8 + (index % 10),
        (index * 7) % 60,
      ),
      category: ['project', 'market', 'internal', 'system'][
        index % 4
      ] as Activity['category'],
      read: index % 4 !== 0,
      tags: [entity.stage, entity.industry],
    };
  });

export const DEFAULT_MODULE_ORDER = [
  'tasks',
  'schedule',
  'highlights',
  'focus',
  'risks',
  'activities',
  'quickActions',
  'businessApps',
];

export const createInitialState = (now = new Date()): WorkbenchState => {
  const base = dayjs(now).startOf('day');
  const sourceEntities = createEntities(base);
  return {
    schemaVersion: 1,
    generatedAt: now.toISOString(),
    sourceEntities,
    workItems: createWorkItems(base, sourceEntities),
    schedules: createSchedules(base, sourceEntities),
    focusObjects: createFocusObjects(base, sourceEntities),
    reminders: createReminders(base, sourceEntities),
    activities: createActivities(base, sourceEntities),
    preference: {
      roleView: 'investmentManager',
      moduleOrder: DEFAULT_MODULE_ORDER,
      hiddenModules: [],
      reminderPreferences: {
        highRisk: true,
        taskDue: true,
        meeting: true,
        decision: true,
        payment: true,
        reporting: true,
        focusedEntity: true,
      },
      homeScope: 'mine',
    },
    viewState: {
      workItemTab: 'all',
      activityTab: 'all',
      focusTab: 'all',
      scheduleView: 'week',
      filters: {},
      scrollPositions: {},
    },
  };
};

/** 运行时初始态：空壳，页面数据由工作台汇总 API 填充，避免演示 mock 混入真实待办。 */
export const createEmptyState = (now = new Date()): WorkbenchState => ({
  schemaVersion: 1,
  generatedAt: now.toISOString(),
  sourceEntities: [],
  workItems: [],
  schedules: [],
  focusObjects: [],
  reminders: [],
  activities: [],
  preference: {
    roleView: 'investmentManager',
    moduleOrder: DEFAULT_MODULE_ORDER,
    hiddenModules: [],
    reminderPreferences: {
      highRisk: true,
      taskDue: true,
      meeting: true,
      decision: true,
      payment: true,
      reporting: true,
      focusedEntity: true,
    },
    homeScope: 'mine',
  },
  viewState: {
    workItemTab: 'all',
    activityTab: 'all',
    focusTab: 'all',
    scheduleView: 'week',
    filters: {},
    scrollPositions: {},
  },
});
